import React, { useState, useRef, useEffect } from 'react'
import { Mic, MicOff, Play, Pause, Square, Upload, Loader2 } from 'lucide-react'
import { Button } from './ui/button'

interface VoiceRecorderProps {
  onStandupSubmitted: (data: any) => void
  userId: string
  teamId: string
  supabaseUrl: string
}

interface RecordingState {
  isRecording: boolean
  isPaused: boolean
  isPlaying: boolean
  isProcessing: boolean
  duration: number
  audioBlob: Blob | null
  audioUrl: string | null
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onStandupSubmitted,
  userId,
  teamId,
  supabaseUrl
}) => {
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    isPlaying: false,
    isProcessing: false,
    duration: 0,
    audioBlob: null,
    audioUrl: null
  })

  const [error, setError] = useState<string | null>(null)
  const [transcript, setTranscript] = useState<string>('')
  const [standupData, setStandupData] = useState<any>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup function
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      if (recordingState.audioUrl) {
        URL.revokeObjectURL(recordingState.audioUrl)
      }
    }
  }, [recordingState.audioUrl])

  const startRecording = async () => {
    try {
      setError(null)
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      })
      
      streamRef.current = stream
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      })
      
      mediaRecorderRef.current = mediaRecorder
      
      const chunks: Blob[] = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data)
        }
      }
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm;codecs=opus' })
        const audioUrl = URL.createObjectURL(audioBlob)
        
        setRecordingState(prev => ({
          ...prev,
          audioBlob,
          audioUrl,
          isRecording: false,
          isPaused: false
        }))
      }
      
      mediaRecorder.start()
      
      setRecordingState(prev => ({
        ...prev,
        isRecording: true,
        duration: 0
      }))
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingState(prev => ({
          ...prev,
          duration: prev.duration + 1
        }))
      }, 1000)
      
    } catch (err) {
      console.error('Error starting recording:', err)
      setError('Failed to start recording. Please check microphone permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingState.isRecording) {
      mediaRecorderRef.current.stop()
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }

  const pauseRecording = () => {
    if (mediaRecorderRef.current && recordingState.isRecording) {
      mediaRecorderRef.current.pause()
      setRecordingState(prev => ({ ...prev, isPaused: true }))
      
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }

  const resumeRecording = () => {
    if (mediaRecorderRef.current && recordingState.isPaused) {
      mediaRecorderRef.current.resume()
      setRecordingState(prev => ({ ...prev, isPaused: false }))
      
      // Resume timer
      timerRef.current = setInterval(() => {
        setRecordingState(prev => ({
          ...prev,
          duration: prev.duration + 1
        }))
      }, 1000)
    }
  }

  const playRecording = () => {
    if (recordingState.audioUrl && audioRef.current) {
      audioRef.current.play()
      setRecordingState(prev => ({ ...prev, isPlaying: true }))
    }
  }

  const pausePlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      setRecordingState(prev => ({ ...prev, isPlaying: false }))
    }
  }

  const resetRecording = () => {
    if (recordingState.audioUrl) {
      URL.revokeObjectURL(recordingState.audioUrl)
    }
    
    setRecordingState({
      isRecording: false,
      isPaused: false,
      isPlaying: false,
      isProcessing: false,
      duration: 0,
      audioBlob: null,
      audioUrl: null
    })
    
    setTranscript('')
    setStandupData(null)
    setError(null)
  }

  const submitVoiceStandup = async () => {
    if (!recordingState.audioBlob) {
      setError('No recording to submit')
      return
    }

    setRecordingState(prev => ({ ...prev, isProcessing: true }))
    setError(null)

    try {
      const formData = new FormData()
      formData.append('audio', recordingState.audioBlob, 'standup.webm')
      formData.append('userId', userId)
      formData.append('teamId', teamId)

      const response = await fetch(`${supabaseUrl}/functions/v1/voice-standup-processor`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Processing failed: ${errorText}`)
      }

      const result = await response.json()
      
      setTranscript(result.transcript)
      setStandupData(result.standupData)
      
      // Notify parent component
      onStandupSubmitted(result.standupData)
      
    } catch (err) {
      console.error('Error submitting voice standup:', err)
      setError(err instanceof Error ? err.message : 'Failed to process voice standup')
    } finally {
      setRecordingState(prev => ({ ...prev, isProcessing: false }))
    }
  }

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    // No border-radius on this card-style container, per UI_STYLE_GUIDE.md §1.
    <div className="border border-border bg-background shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-foreground">Voice Standup</h3>
        <div className="text-sm text-muted-foreground">
          {recordingState.duration > 0 && formatDuration(recordingState.duration)}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 border border-destructive/30 bg-destructive/10">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Recording Controls */}
      <div className="flex items-center space-x-4 mb-6">
        {!recordingState.isRecording && !recordingState.audioBlob && (
          <Button
            onClick={startRecording}
            size="lg"
            className="min-h-11 gap-2 bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            <Mic className="w-4 h-4" />
            <span>Start Recording</span>
          </Button>
        )}

        {recordingState.isRecording && !recordingState.isPaused && (
          <>
            <Button
              onClick={pauseRecording}
              variant="outline"
              size="lg"
              className="min-h-11 gap-2"
            >
              <Pause className="w-4 h-4" />
              <span>Pause</span>
            </Button>
            <Button
              onClick={stopRecording}
              variant="secondary"
              size="lg"
              className="min-h-11 gap-2"
            >
              <Square className="w-4 h-4" />
              <span>Stop</span>
            </Button>
          </>
        )}

        {recordingState.isPaused && (
          <>
            <Button
              onClick={resumeRecording}
              size="lg"
              className="min-h-11 gap-2"
            >
              <Mic className="w-4 h-4" />
              <span>Resume</span>
            </Button>
            <Button
              onClick={stopRecording}
              variant="secondary"
              size="lg"
              className="min-h-11 gap-2"
            >
              <Square className="w-4 h-4" />
              <span>Stop</span>
            </Button>
          </>
        )}

        {recordingState.audioBlob && !recordingState.isRecording && (
          <>
            <Button
              onClick={recordingState.isPlaying ? pausePlayback : playRecording}
              size="lg"
              className="min-h-11 gap-2"
            >
              {recordingState.isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{recordingState.isPlaying ? 'Pause' : 'Play'}</span>
            </Button>
            <Button
              onClick={resetRecording}
              variant="secondary"
              size="lg"
              className="min-h-11 gap-2"
            >
              <MicOff className="w-4 h-4" />
              <span>Reset</span>
            </Button>
            <Button
              onClick={submitVoiceStandup}
              disabled={recordingState.isProcessing}
              size="lg"
              className="min-h-11 gap-2"
            >
              {recordingState.isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              <span>{recordingState.isProcessing ? 'Processing...' : 'Submit Standup'}</span>
            </Button>
          </>
        )}
      </div>

      {/* Audio Player */}
      {recordingState.audioUrl && (
        <audio
          ref={audioRef}
          src={recordingState.audioUrl}
          onEnded={() => setRecordingState(prev => ({ ...prev, isPlaying: false }))}
          className="hidden"
        />
      )}

      {/* Recording Indicator. bg-destructive (not raw red-500) -- same token
          already used above for the Start Recording button, per
          UI_STYLE_GUIDE.md §7. */}
      {recordingState.isRecording && (
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-3 h-3 bg-destructive rounded-full animate-pulse"></div>
          <span className="text-sm text-muted-foreground">
            {recordingState.isPaused ? 'Recording paused' : 'Recording...'}
          </span>
        </div>
      )}

      {/* Transcript Display. No border-radius, per §1 -- bg-muted (not
          bg-gray-50) so this reads as a neutral inset block, not a card. */}
      {transcript && (
        <div className="mt-6 p-4 bg-muted">
          <h4 className="text-sm font-medium text-foreground mb-2">Transcript:</h4>
          <p className="text-sm text-muted-foreground">{transcript}</p>
        </div>
      )}

      {/* Processed Standup Data. Previously each field (Yesterday/Today/
          Blockers/Summary) had its own rainbow-coded bg-blue-50/bg-green-50/
          bg-yellow-50 callout -- none of that maps to an actual app-wide
          status (unlike, say, an on-time/missed pill), so it's flattened to
          the same neutral bg-muted block as every other inset panel in the
          app, per UI_STYLE_GUIDE.md §1/§7. */}
      {standupData && (
        <div className="mt-6 space-y-4">
          <h4 className="text-sm font-medium text-foreground">Extracted Standup Information:</h4>

          <div className="space-y-3">
            <div className="p-3 bg-muted">
              <h5 className="text-sm font-medium text-foreground">Yesterday:</h5>
              <p className="text-sm text-muted-foreground">{standupData.questions?.yesterday}</p>
            </div>

            <div className="p-3 bg-muted">
              <h5 className="text-sm font-medium text-foreground">Today:</h5>
              <p className="text-sm text-muted-foreground">{standupData.questions?.today}</p>
            </div>

            <div className="p-3 bg-muted">
              <h5 className="text-sm font-medium text-foreground">Blockers:</h5>
              <p className="text-sm text-muted-foreground">{standupData.questions?.blockers}</p>
            </div>
          </div>

          {standupData.summary && (
            <div className="p-3 bg-muted">
              <h5 className="text-sm font-medium text-foreground">Summary:</h5>
              <p className="text-sm text-muted-foreground">{standupData.summary}</p>
            </div>
          )}

          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <span>Sentiment: {standupData.sentiment}</span>
            <span>Confidence: {Math.round((standupData.confidence || 0) * 100)}%</span>
          </div>
        </div>
      )}

      {/* Instructions. No border-radius, per §1; bg-muted instead of
          bg-blue-50 -- not a status callout, just a neutral inset block. */}
      {!recordingState.isRecording && !recordingState.audioBlob && (
        <div className="mt-6 p-4 bg-muted">
          <h4 className="text-sm font-medium text-foreground mb-2">How to use Voice Standup:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Click "Start Recording" and speak naturally</li>
            <li>• Mention what you did yesterday, plan for today, and any blockers</li>
            <li>• You can pause/resume during recording</li>
            <li>• Review your recording before submitting</li>
            <li>• AI will extract and structure your standup information</li>
          </ul>
        </div>
      )}
    </div>
  )
}

export default VoiceRecorder 